<template>
  <div class="support-page">
    <component :is="'script'" type="application/ld+json" v-text="faqJsonLd" />

    <section class="hero">
      <img
        class="hero-bg"
        src="https://dronecloud-files.s3.cn-northwest-1.amazonaws.com.cn/wxapp/static/images/guanwang/images/solution/question_banner.png"
        alt=""
      />
      <div class="hero-inner">
        <div class="title">常见问题</div>
        <div class="desc">高频问题一键直达，告别繁琐咨询等待</div>
      </div>
    </section>

    <section class="faq-section" id="faq">
      <div class="container faq-layout">
        <aside class="faq-sidebar" aria-label="问题类别">
          <h2 class="sidebar-title">问题类别</h2>
          <nav class="category-list" role="tablist">
            <button
              v-for="item in categories"
              :key="item.key"
              type="button"
              class="category-item"
              :class="{ active: selectedCategory === item.key }"
              role="tab"
              :aria-selected="selectedCategory === item.key"
              @click="selectCategory(item.key)"
            >
              <span>{{ item.label }}</span>
              <i
                class="el-icon-right"
                style="font-size: 20px"
                v-if="selectedCategory === item.key"
              ></i>
            </button>
          </nav>
        </aside>

        <div class="faq-main" role="tabpanel">
          <!-- 供爬虫一次拿到-->
          <div
            v-for="(item, index) in faqs"
            :key="index + '-' + item.question"
            class="faq-item"
            :class="{ open: activeFaqIndex === index }"
            :data-category="item.category"
            :hidden="isFaqHidden(item)"
          >
            <button
              type="button"
              class="faq-question"
              :aria-expanded="activeFaqIndex === index ? 'true' : 'false'"
              @click="toggleFaq(index)"
            >
              <span class="faq-question-text">{{ item.question }}</span>
              <span class="faq-toggle" aria-hidden="true">{{
                activeFaqIndex === index ? "−" : "+"
              }}</span>
            </button>
            <p class="faq-answer">{{ item.answer }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="contact-section">
      <div class="container">
        <div class="contact-band">
          <img
            class="contact-bg"
            src="https://dronecloud-files.s3.cn-northwest-1.amazonaws.com.cn/wxapp/static/images/guanwang/images/solution/question_contactus_bg.png"
            alt=""
          />
          <div class="contact-copy">
            <div class="contact-zh">更多问题请联系我们</div>
            <button type="button" class="contact-btn" @click="goContact">
              联系我们
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  SUPPORT_SEO,
  SUPPORT_CATEGORIES,
  SUPPORT_FAQS,
} from "../../data/support-faq.js";
import { setPageSeo } from "../../util/seo.js";

export default {
  name: "TechnicalSupport",
  data() {
    return {
      selectedCategory: "survey",
      activeFaqIndex: 0,
      categories: SUPPORT_CATEGORIES,
      faqs: SUPPORT_FAQS,
    };
  },
  computed: {
    faqJsonLd() {
      return JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: this.faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      });
    },
  },
  mounted() {
    setPageSeo(SUPPORT_SEO);
    this.activeFaqIndex = this.firstIndexForCategory(this.selectedCategory);
  },
  methods: {
    isFaqHidden(item) {
      return this.selectedCategory !== item.category;
    },
    firstIndexForCategory(key) {
      return this.faqs.findIndex((item) => item.category === key);
    },
    selectCategory(key) {
      this.selectedCategory = key;
      this.activeFaqIndex = this.firstIndexForCategory(key);
    },
    toggleFaq(index) {
      this.activeFaqIndex = this.activeFaqIndex === index ? -1 : index;
    },
    goContact() {
      this.$router.push("/about/company-address");
    },
  },
};
</script>

<style lang="stylus" scoped>
.support-page {
  --blue: #2372E9;
  --navy: #1f2d3d;
  --text: #333;
  --muted: #666;
  --line: #e8ecf2;
  --soft: #f5f7fa;
  color: var(--text);
  background: #fff;

  .container {
    max-width: var(--main-width);
    width: 84%;
    margin: 0 auto;
  }

  .hero {
    position: relative;
    margin-top: 80px;
    line-height: 0;
  }

  .hero-bg {
    display: block;
    width: 100%;
    height: auto;
  }

  .hero-inner {
    position: absolute;
    left: 50%;
    top: 50%;
    z-index: 1;
    width: 84%;
    max-width: var(--main-width);
    padding: 0;
    text-align: center;
    color: #fff;
    line-height: normal;
    transform: translate(-50%, -50%);

    .title {
      margin: 0 0 1.44rem;
      font-size: 3rem;
      letter-spacing: 2px;
      font-weight: bold;
      line-height: 1.2;
    }

    .desc {
      letter-spacing: 2px;
      font-size: 1.25rem;
      line-height: 2.2rem;
    }
  }

  .faq-section {
    padding: 56px 0 28px;
    background: #fff;
    margin-bottom:6rem;
  }

  .faq-layout {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 28px;
    align-items: stretch;
  }

  .faq-sidebar {
    height: 100%;
    background: #f5f7fa;
    border-radius: 4px;
    padding: 28px 0 20px;
  }

  .sidebar-title {
    margin: 0 0 12px;
    padding: 0 28px;
    color: var(--navy);
    font-size: 20px;
    font-weight: 700;
    line-height: 1.4;
  }

  .category-list {
    display: flex;
    flex-direction: column;
  }

  .category-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 52px;
    padding: 0 28px;
    border: 0;
    background: transparent;
    color: #555;
    font-size: 16px;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: color 0.2s ease;

    i {
      font-size: 14px;
      color: var(--blue);
    }

    &:hover {
      color: var(--blue);
    }

    &.active {
      color: var(--blue);
      font-weight: 600;
    }
  }

  .faq-main {
    background: transparent;
    padding: 8px 0 20px 8px;
    min-height: 310px;
  }

  .faq-item {
    border-bottom: 1px solid var(--line);

    &[hidden] {
      display: none !important;
    }

    &:last-child {
      border-bottom: 0;
    }
  }

  .faq-question {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    width: 100%;
    min-height: 64px;
    padding: 18px 0;
    border: 0;
    background: transparent;
    color: var(--navy);
    font-size: 16px;
    font-weight: 500;
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    line-height: 1.5;
  }

  .faq-question-text {
    flex: 1;
  }

  .faq-toggle {
    flex-shrink: 0;
    width: 24px;
    color: var(--blue);
    font-size: 26px;
    font-weight: 400;
    line-height: 1;
    text-align: center;
  }

  .faq-item.open {
    .faq-question {
      color: var(--blue);
    }

    .faq-answer {
      display: block;
    }
  }

  .faq-answer {
    display: none;
    margin: 0;
    padding: 0 32px 22px 0;
    color: var(--muted);
    font-size: 15px;
    line-height: 1.75;
  }

  .contact-section {
    padding: 0 0 72px;
    background: #fff;
  }

  .contact-band {
    position: relative;
    overflow: hidden;
    border-radius: 4px;
    line-height: 0;
  }

  .contact-bg {
    display: block;
    width: 100%;
    height: auto;
  }

  .contact-copy {
    position: absolute;
    left: 0;
    top: 50%;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
    padding: 0 56px;
    transform: translateY(-50%);
    line-height: normal;
  }

  .contact-zh {
    color: #fff;
    font-size: 28px;
    font-weight: 700;
    line-height: 1.4;
  }

  .contact-btn {
    min-width: 128px;
    min-height: 44px;
    padding: 0 24px;
    border: 0;
    border-radius: 4px;
    background: var(--blue);
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;

    &:hover {
      opacity: 0.92;
    }
  }
}

@media (max-width: 900px) {
  .support-page {
    .hero {
      margin-top: 128px;
    }

    .hero-inner {
      .title {
        font-size: 24px;
        margin-bottom: 10px;
      }

      .desc {
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 1px;
      }
    }

    .faq-section {
      padding: 32px 0 48px;
      margin-bottom: 2.5rem;
    }

    .faq-layout {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .faq-sidebar {
      padding: 20px 0 12px;
    }

    .sidebar-title {
      font-size: 18px;
      padding: 0 20px;
    }

    .category-item {
      min-height: 44px;
      padding: 0 20px;
      font-size: 15px;
    }

    .faq-main {
      padding: 4px 0 12px;
    }

    .faq-question {
      min-height: 52px;
      font-size: 14px;
    }

    .faq-answer {
      padding: 0 0 18px;
      font-size: 14px;
    }

    .contact-section {
      padding: 0 0 40px;
    }

    .contact-band {
      display: flex;
      align-items: center;
      min-height: 120px;
    }

    .contact-bg {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center right;
    }

    .contact-copy {
      position: relative;
      top: auto;
      transform: none;
      width: 100%;
      box-sizing: border-box;
      padding: 24px 16px;
      gap: 12px;
    }

    .contact-zh {
      font-size: 18px;
      line-height: 1.35;
      max-width: 70%;
    }

    .contact-btn {
      min-width: 104px;
      min-height: 36px;
      padding: 0 18px;
      font-size: 14px;
    }
  }
}
</style>
